import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Stat, StatGroup, EmptyState, BarList, type BarListItem } from '@vnetwork/ui/data';
import { Chart, type ChartSeries } from '@vnetwork/ui/charts';
import { Alert } from '@vnetwork/ui/feedback';
import type { AnalyticsPeriod, Granularity, TimeRange } from '@/api/manticore';
import { DateTimeRangePicker } from '@/components/date-time-range-picker/DateTimeRangePicker';

// Shared building blocks for the central-analytics pages (Analytics + the
// Manticore overview dashboard). Token-disciplined: real `--vn-*` only.

export interface PeriodOpt {
  value: AnalyticsPeriod;
  label: string;
}

/** Vertical stack with consistent design-system spacing between blocks. */
export function Stack({
  children,
  gap = 'var(--vn-space-4)',
  style,
}: {
  children: ReactNode;
  gap?: string;
  style?: CSSProperties;
}) {
  return <div style={{ display: 'grid', gap, ...style }}>{children}</div>;
}

/** Responsive auto-fit grid of cards. */
export function CardGrid({ children, min = 300 }: { children: ReactNode; min?: number }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))` }}>
      {children}
    </div>
  );
}

/** Period switcher (SegmentedControl). */
export function PeriodControl({
  options,
  value,
  onChange,
}: {
  options: PeriodOpt[];
  value: AnalyticsPeriod;
  onChange: (p: AnalyticsPeriod) => void;
}) {
  return (
    <SegmentedControl
      aria-label="Time range"
      size="md"
      items={options.map((o) => ({ value: o.value, label: o.label }))}
      value={value}
      onValueChange={(v) => onChange(v as AnalyticsPeriod)}
    />
  );
}

// ---- Time range (presets + custom) ----------------------------------------

/**
 * Time-range switcher — the quick-preset SegmentedControl stays inline for
 * one-click ranges, with the shared `DateTimeRangePicker` alongside as the
 * "Custom" affordance (calendar with the selected range highlighted + per-endpoint
 * time). A custom pick deselects the presets; picking a preset clears the custom
 * window. The end time defaults to 23:59 so picking an end day counts through the
 * whole of that day — one inclusive-last-day basis across every analytics view.
 */
export function RangeControl({
  options,
  value,
  onChange,
}: {
  options: PeriodOpt[];
  value: TimeRange;
  onChange: (r: TimeRange) => void;
}) {
  const isCustom = value.kind === 'custom';
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <SegmentedControl
        aria-label="Time range"
        size="md"
        items={options.map((o) => ({ value: o.value, label: o.label }))}
        value={isCustom ? '' : value.period}
        onValueChange={(v) => onChange({ kind: 'preset', period: v as AnalyticsPeriod })}
      />
      <DateTimeRangePicker
        value={isCustom ? { from: value.from, to: value.to } : { from: '', to: '' }}
        onChange={(v) => {
          if (v.from && v.to) onChange({ kind: 'custom', from: v.from, to: v.to });
        }}
        placeholder="Custom"
      />
    </div>
  );
}

const GRAN_LABEL: Record<Granularity, string> = { '5m': '5m', '1h': '1h', day: 'Day' };

/**
 * Granularity switcher — shown next to the period control for short windows so
 * the user can drop to the 5m tier (matches the vendor portal's interval picker).
 * Render only when `options` has more than one tier.
 */
export function GranularityControl({
  options,
  value,
  onChange,
}: {
  options: Granularity[];
  value: Granularity;
  onChange: (g: Granularity) => void;
}) {
  if (options.length < 2) return null;
  return (
    <SegmentedControl
      aria-label="Granularity"
      size="md"
      items={options.map((g) => ({ value: g, label: GRAN_LABEL[g] }))}
      value={value}
      onValueChange={(v) => onChange(v as Granularity)}
    />
  );
}

const AUTO_REFRESH_OPTS = [
  { value: '0', label: 'Auto-refresh: Off' },
  { value: '30', label: 'Every 30s' },
  { value: '60', label: 'Every 1m' },
  { value: '300', label: 'Every 5m' },
];

/** Manual refresh — a proper secondary button (icon + label) that spins briefly on click. */
export function RefreshButton({ onRefresh }: { onRefresh: () => void }) {
  const [spinning, setSpinning] = useState(false);
  const fire = () => {
    onRefresh();
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 600);
  };
  return (
    <Button
      variant="secondary"
      size="md"
      startIcon={<RefreshCw size={14} style={spinning ? { animation: 'spin 0.6s linear' } : undefined} />}
      onClick={fire}
    >
      Refresh
    </Button>
  );
}

/**
 * Refresh cluster for real-time views: an auto-refresh interval picker + the
 * manual Refresh button. When an interval is chosen it polls `onRefresh` on a
 * timer; the button always triggers an immediate refresh.
 */
export function RefreshControl({
  onRefresh,
  onIntervalChange,
}: {
  onRefresh: () => void;
  /**
   * Opt-in: report the chosen interval (ms; 0 = off) so a parent can drive polling
   * at the query level (e.g. react-query `refetchInterval`). When provided, the
   * parent owns polling and the internal timer is skipped to avoid double-fetching.
   */
  onIntervalChange?: (ms: number) => void;
}) {
  const [interval, setIntervalSec] = useState('0');

  // Keep the latest callback without resetting the timer each render.
  const saved = useRef(onRefresh);
  useEffect(() => {
    saved.current = onRefresh;
  });
  useEffect(() => {
    onIntervalChange?.(Number(interval) * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);
  useEffect(() => {
    if (onIntervalChange) return; // parent drives polling
    if (interval === '0') return;
    const id = window.setInterval(() => saved.current(), Number(interval) * 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);

  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <Select value={interval} onValueChange={setIntervalSec}>
        <SelectTrigger size="md" style={{ minWidth: 0, width: 150, maxWidth: '100%' }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AUTO_REFRESH_OPTS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <RefreshButton onRefresh={onRefresh} />
    </div>
  );
}

export interface Kpi {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  dotColor?: string;
}

/** Row of headline metrics. */
export function KpiTiles({ items, columns = 4, loading }: { items: Kpi[]; columns?: number; loading?: boolean }) {
  if (loading) return <Skeleton style={{ height: 92 }} />;
  return (
    <StatGroup columns={columns}>
      {items.map((k) => (
        <Stat key={k.label} label={k.label} value={k.value} hint={k.hint} dotColor={k.dotColor} />
      ))}
    </StatGroup>
  );
}

/**
 * A titled time-series chart card, with a graceful empty state.
 *
 * Two rendering modes (additive — backward-compatible):
 *  - Chart-props mode (existing): pass `data`, `xKey`, `series` → kit renders
 *    the `<Chart>` with loading / empty-state handling.
 *  - Children mode (new): pass `children` → rendered verbatim inside CardBody;
 *    caller owns loading / empty-state logic inside children.
 *
 * `error` (optional): when truthy shows an inline `Alert tone="warning"` in
 * CardBody, suppressing chart / children. Matches the WAAP per-widget pattern.
 */
/**
 * True when at least one non-x numeric value in the rows is non-zero. A series
 * of all-zero months renders as a bare empty grid that reads as broken — use
 * this to fall back to the EmptyState instead ("no usage" is data too, but the
 * empty state says it explicitly).
 */
export function hasSeriesData<T extends object>(rows: readonly T[], xKey: string): boolean {
  return rows.some((r) =>
    Object.entries(r).some(([k, v]) => k !== xKey && typeof v === 'number' && v !== 0),
  );
}

export function ChartCard({
  title,
  description,
  data = [],
  xKey = '',
  series = [],
  type = 'area',
  height = 220,
  stacked,
  showLegend,
  valueFormatter,
  loading,
  actions,
  children,
  error,
}: {
  title: string;
  description?: string;
  data?: Record<string, unknown>[];
  xKey?: string;
  series?: ChartSeries[];
  type?: 'area' | 'bar' | 'line';
  height?: number;
  stacked?: boolean;
  /** Override the legend (defaults to on for 2+ series). */
  showLegend?: boolean;
  valueFormatter?: (v: string | number | undefined, key?: string, ctx?: { total: number }) => ReactNode;
  loading?: boolean;
  actions?: ReactNode;
  /** Verbatim slot rendered instead of the chart — caller drives loading/empty state. */
  children?: ReactNode;
  /** When truthy, renders an inline warning Alert instead of chart / children. */
  error?: string | boolean;
}) {
  const hasData = data.length > 0 && series.length > 0;
  return (
    <Card flat>
      <CardHeader actions={actions}>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardBody>
        {error ? (
          <Alert tone="warning">
            {typeof error === 'string' ? error : 'Failed to load chart data.'}
          </Alert>
        ) : children !== undefined ? (
          children
        ) : loading ? (
          <Skeleton style={{ height }} />
        ) : hasData ? (
          <Chart
            type={type}
            data={data}
            xKey={xKey}
            series={series}
            height={height}
            stacked={stacked}
            showGrid
            showLegend={showLegend ?? series.length > 1}
            valueFormatter={valueFormatter}
          />
        ) : (
          <EmptyState size="sm" title="No data" description="No samples in the selected range." />
        )}
      </CardBody>
    </Card>
  );
}

/** Ranked "top-N" card (countries, providers, …). */
export function BarCard({
  title,
  description,
  items,
  loading,
}: {
  title: string;
  description?: string;
  items: BarListItem[];
  loading?: boolean;
}) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardBody>
        {loading ? (
          <Skeleton style={{ height: 160 }} />
        ) : items.length === 0 ? (
          <EmptyState size="sm" title="No data" description="No data for this period." />
        ) : (
          <BarList items={items} />
        )}
      </CardBody>
    </Card>
  );
}
